import { useCallback, useMemo } from 'react';
import { defineMessages } from 'react-intl';
import { useTheme } from 'styled-components';
import { Accordion, AccordionBody, AccordionHeader, useAccordion } from '../../Accordion';
import { Button } from '../../Button';
import { Box, Flex } from '../../Core';
import { FormattedMessage } from '../../Intl';
import { Text } from '../../Text';
import { Checkbox } from '../Checkbox';
import { FormControlSizes } from '../types';
import { AutocompleteGroupItemLabel, AutocompleteGroupLabel } from './styles';

const messages = defineMessages({
  clearAll: {
    defaultMessage: 'Clear all',
    id: 'Form.AutocompleteGroupDropdown.clearAll',
  },
  selectAll: {
    defaultMessage: 'Select all',
    id: 'Form.AutocompleteGroupDropdown.selectAll',
  },
});

export interface AutocompleteGroupItem<T> {
  group?: string;
  items: Array<AutocompleteGroupItem<T> | T>;
}

interface AutocompleteGroupResultsProps<T> {
  items: AutocompleteGroupItem<T>[];
  getLabel: (item: T) => string;
  getValue: (item: T) => string;
  selectedItems: string[];
  onSelectedItemChange: (selectedItem: T) => void;
  onSelectedGroupChange: (group: string, checked: boolean) => void;
}

export const AutocompleteGroupResults = <T extends object>(props: AutocompleteGroupResultsProps<T>) => {
  return (
    <Flex flexDirection="column" h="250px" overflow="auto" background="colors.gray['040']">
      <AutocompleteGroupRenderItems {...props} level={1} />
    </Flex>
  );
};

const AutocompleteGroupRenderItems = <T extends object>(
  props: AutocompleteGroupItem<T> & {
    selectedItems: string[];
    getLabel: (item: T) => string;
    getValue: (item: T) => string;
    onSelectedItemChange: (selectedItem: T) => void;
    onSelectedGroupChange: (group: string, checked: boolean) => void;
    level: number;
  }
) => {
  const theme = useTheme();
  const { items, selectedItems, level, getLabel, getValue, onSelectedItemChange } = props;

  const handleCheckboxChange = useCallback(
    (selectedItem: T) => {
      onSelectedItemChange(selectedItem);
    },
    [onSelectedItemChange]
  );

  return (
    <>
      {items.map((item, index) => {
        const group = 'group' in item ? item.group : null;
        const groupItems = 'items' in item ? item.items : [];
        const isSelected = !group && selectedItems.some(selectedItem => selectedItem === getValue(item as T));
        const isLast = index === items.length - 1;

        return (
          <div key={`item-${level}-${index}`}>
            {group ? (
              <AutocompleteRenderGroup
                {...props}
                group={group}
                items={groupItems}
                selectedItems={selectedItems}
                level={level}
              />
            ) : (
              <AutocompleteGroupItemLabel showBorder={level === 2} showConnected={level > 2 && !isLast}>
                <Box
                  p={level > 2 ? `${theme.spacing4}px ${theme.spacing8}px` : 'spacing8'}
                  background={isSelected ? 'colors.gray["050"]' : 'transparent'}
                >
                  <Checkbox onChange={() => handleCheckboxChange(item as T)} checked={isSelected}>
                    {getLabel(item as T)}
                  </Checkbox>
                </Box>
              </AutocompleteGroupItemLabel>
            )}
          </div>
        );
      })}
    </>
  );
};

const AutocompleteRenderGroup = <T extends object>(
  props: AutocompleteGroupItem<T> & {
    selectedItems: string[];
    getLabel: (item: T) => string;
    getValue: (item: T) => string;
    onSelectedItemChange: (selectedItem: T) => void;
    onSelectedGroupChange: (group: string, checked: boolean) => void;
    level: number;
  }
) => {
  const theme = useTheme();
  const { level, group, items, onSelectedGroupChange, selectedItems, getValue } = props;
  const accordion = useAccordion({ id: `group-${group}`, ghost: true, initialOpen: true });
  const allChecked: boolean = useMemo(() => {
    const groupChecked = (items: Array<AutocompleteGroupItem<T> | T>, subgroup: boolean): boolean => {
      return items[subgroup ? 'some' : 'every'](item => {
        const group = 'group' in item ? item.group : null;
        if (group) {
          const groupItems = 'items' in item ? item.items : [];
          return groupChecked(groupItems, true);
        } else {
          return selectedItems.some(selectedItem => selectedItem === getValue(item as T));
        }
      });
    };
    return level === 1 ? groupChecked(items, false) : false;
  }, [selectedItems, getValue, level, items]);

  const selectableCount = useMemo(() => {
    return items.length;
  }, [items]);

  const selectedCount = useMemo(() => {
    return items.flatMap(item => (item as any)?.items ?? item).filter(item => selectedItems.includes(getValue(item)))
      .length;
  }, [selectedItems, items, getValue]);

  const isSelected = useMemo(() => {
    return items.some(item => selectedItems.includes(getValue(item as T)));
  }, [items, selectedItems, getValue]);

  return (
    <>
      {level === 1 && (
        <Accordion {...accordion}>
          <AccordionHeader
            position="sticky"
            top="0"
            zIndex={3}
            background="colors.gray['050']"
            data-testid="autocomplete-group-header"
            fontSize="fontSizeSmall"
            alignChevron="left"
            p={`${theme.spacing4}px ${theme.spacing8}px`}
            suffix={
              <Button
                ghost
                size={FormControlSizes.Tiny}
                onClick={e => {
                  onSelectedGroupChange(group!, !allChecked);
                  e.stopPropagation();
                }}
              >
                <Text transform="uppercase">
                  <FormattedMessage {...messages[!allChecked ? 'selectAll' : 'clearAll']} />
                </Text>
              </Button>
            }
          >
            {group} ({selectedCount}/{selectableCount})
          </AccordionHeader>
          <AccordionBody p={0}>
            <AutocompleteGroupRenderItems {...props} level={level + 1} />
          </AccordionBody>
        </Accordion>
      )}
      {level > 1 && (
        <Box background={isSelected ? 'colors.gray["050"]' : 'transparent'}>
          <AutocompleteGroupLabel>{group}</AutocompleteGroupLabel>
          <AutocompleteGroupRenderItems {...props} level={level + 1} />
        </Box>
      )}
    </>
  );
};
